import { atom, computed, map } from 'nanostores';

import { ApiError, api, readToken, writeToken, type Plan, type User } from '../lib/api';

import { track } from '../analytics/track';

interface AuthState {
  user: User | null;
  plan: Plan | null;
  activeKeys: number;
  /** null until the first /api/me settles, so guards can show a spinner. */
  ready: boolean;
  error: string | null;
}

export const $auth = map<AuthState>({
  user: null,
  plan: null,
  activeKeys: 0,
  ready: false,
  error: null,
});

export const $authPending = atom(false);
export const $isAuthenticated = computed($auth, (s) => s.user !== null);

/**
 * Bumped every time the session identity changes. A request that was already in
 * flight when the user signed out must not write its answer into stores that no
 * longer belong to anyone, so every loader captures the epoch before its first
 * await and drops the result if it has moved on.
 */
let epoch = 0;

export function sessionEpoch(): number {
  return epoch;
}

export function isStaleSession(captured: number): boolean {
  return captured !== epoch;
}

/**
 * Per-user state lives in sibling stores that must not outlive the session —
 * without this the next render still shows the previous user's plan, keys and
 * usage until a full page load throws them away.
 */
const clearHandlers = new Set<() => void>();

export function onSessionCleared(handler: () => void): void {
  clearHandlers.add(handler);
}

function clearSessionState(): void {
  epoch += 1;
  for (const handler of clearHandlers) handler();
}

/** Resolve the stored token into a session. Safe to call repeatedly. */
export async function hydrateSession(): Promise<void> {
  if (!readToken()) {
    $auth.setKey('ready', true);
    return;
  }
  const captured = epoch;
  try {
    const me = await api.me();
    if (isStaleSession(captured)) return;
    $auth.set({ user: me.user, plan: me.plan, activeKeys: me.active_keys, ready: true, error: null });
  } catch (err) {
    if (isStaleSession(captured)) return;
    // An expired or tampered token is indistinguishable from a logged-out
    // visitor as far as the UI cares — drop it and move on.
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      writeToken(null);
    }
    $auth.set({ user: null, plan: null, activeKeys: 0, ready: true, error: null });
  }
}

export async function login(email: string, password: string): Promise<void> {
  $authPending.set(true);
  $auth.setKey('error', null);
  try {
    const res = await api.login({ email, password });
    clearSessionState();
    writeToken(res.token);
    $auth.set({ user: res.user, plan: res.plan, activeKeys: 0, ready: true, error: null });
    track('signup_completed');
    await refreshMe();
  } catch (err) {
    $auth.setKey('error', messageOf(err));
    throw err;
  } finally {
    $authPending.set(false);
  }
}

export async function register(input: {
  email: string;
  password: string;
  name: string;
  company: string;
}): Promise<void> {
  $authPending.set(true);
  $auth.setKey('error', null);
  try {
    const res = await api.register(input);
    clearSessionState();
    writeToken(res.token);
    $auth.set({ user: res.user, plan: res.plan, activeKeys: 0, ready: true, error: null });
    await refreshMe();
  } catch (err) {
    $auth.setKey('error', messageOf(err));
    throw err;
  } finally {
    $authPending.set(false);
  }
}

export function logout(): void {
  clearSessionState();
  writeToken(null);
  $auth.set({ user: null, plan: null, activeKeys: 0, ready: true, error: null });
}

/** Re-reads the session; call after anything that can change plan or key count. */
export async function refreshMe(): Promise<void> {
  const captured = epoch;
  try {
    const me = await api.me();
    if (isStaleSession(captured)) return;
    $auth.set({ user: me.user, plan: me.plan, activeKeys: me.active_keys, ready: true, error: null });
  } catch {
    /* keep the last known good state rather than flashing an error */
  }
}

export function messageOf(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
